import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ISupportingActor,
  PARTICIPANT_ACTOR_KEY,
  SupportingActorType
} from '@dadohr/shared/domainTypes/experience';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, ButtonBase, Tooltip, Typography } from '@material-ui/core';
import { keyBy, sortBy, uniqBy } from 'lodash';
import { Check, Eye } from 'lucide-react';
import React, { useMemo } from 'react';
import { AutocompleteSingle } from '../../../../components/Autocomplete';
import { WithShape } from '../../../../components/Charts/Util';
import { ChipWithAction } from '../../../../components/ChipWithAction';
import { CustomInputLabel } from '../../../../components/CustomInputLabel';
import { useTranslation, useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { COLOR_UNKNOWN } from '../../../../services/color';
import { getSelectableSupportingActors } from '../../../../services/experiences/actorUsage';
import { ActorChip } from '../Builders/newux/kit';
import {
  getUsersByOrgId,
  matchUserBySearchTerm,
  toFullName,
  toSearchTermRegexp
} from '../../../../services/user';

export type ActorDescriptor = {
  type: SupportingActorType | 'AUTOMATION' | 'PARTICIPANT' | 'OTHER';
  key: string;
  label: string;
  color: string;
};

export const EMPTY_ACTOR_DESCRIPTOR = (actorKey: string) => ({
  key: actorKey,
  label: '',
  color: COLOR_UNKNOWN
});

export type ActorDescriptorExtended = ActorDescriptor & {
  user: Doc<IUser> | null;
};

export const usePartcipantActorDescriptor = (): ActorDescriptor => {
  const {
    custom: { colors }
  } = useTheme();
  const { t } = useTranslation();
  const label = t('Participant');
  const color = colors.blue.w400;
  return useMemo(
    () => ({
      key: PARTICIPANT_ACTOR_KEY,
      label,
      color,
      type: 'PARTICIPANT' // Hmm a bit of a lie
    }),
    [label, color]
  );
};

/**
 * Supporting actor color palette (v4) — ordered by distinctiveness (the 9 most visually
 * distinct colors first, so small actor sets stay maximally distinguishable), light
 * backgrounds only. Each has a dark companion (same hue, darkened) kept in
 * ACTOR_COLORS_DARK for a possible future design need — not used in actor assignment.
 */
export const ACTOR_COLORS_LIGHT: { name: string; hex: string }[] = [
  { name: 'Coral', hex: '#f8ccbf' },
  { name: 'Teal', hex: '#ccf0ef' },
  { name: 'Blue', hex: '#bfd9f7' },
  { name: 'Amber', hex: '#f8ddbf' },
  { name: 'Mulberry', hex: '#e8b0d3' },
  { name: 'Rose', hex: '#f3c8d3' },
  { name: 'Yellow', hex: '#fff1b8' },
  { name: 'Periwinkle', hex: '#c2bcdd' },
  { name: 'Sage', hex: '#dbebdd' },
  { name: 'Turquoise', hex: '#caece4' },
  { name: 'Terracotta', hex: '#f1d9d0' },
  { name: 'Cocoa', hex: '#e2d0c5' },
  { name: 'Ocean', hex: '#b7dff0' },
  { name: 'Umber', hex: '#e4c8af' },
  { name: 'Denim', hex: '#dce2ef' },
  { name: 'Sand', hex: '#f0e6d6' },
  { name: 'Mustard', hex: '#efe3bd' },
  { name: 'Cobalt', hex: '#b1baf1' },
  { name: 'Moss', hex: '#dfebd6' },
  { name: 'Wine', hex: '#e8c9ce' }
];

/** Dark companion for each ACTOR_COLORS_LIGHT entry — not part of actor assignment. */
export const ACTOR_COLORS_DARK: Record<string, string> = {
  Coral: '#ab310d',
  Teal: '#269794',
  Blue: '#0f56a8',
  Amber: '#ab600d',
  Mulberry: '#7b1e58',
  Rose: '#9f1d3e',
  Yellow: '#b89400',
  Periwinkle: '#3a306a',
  Sage: '#42854a',
  Turquoise: '#2b8c75',
  Terracotta: '#9b4727',
  Cocoa: '#754c33',
  Ocean: '#186c90',
  Umber: '#734821',
  Denim: '#3a5692',
  Sand: '#976f30',
  Mustard: '#90741d',
  Cobalt: '#14268f',
  Moss: '#5b883a',
  Wine: '#82303d'
};

export const useActorDescriptorsFromTemplate = (
  supportingActors: {
    [key: string]: ISupportingActor;
  },
  includeChannelActors: boolean = true,
  // Historical/already-referenced displays (e.g. a past Reports row) should
  // still resolve an archived actor's name; selection surfaces (dropdowns,
  // legends) should not offer it as a new pick, which is why this defaults
  // to false.
  includeArchived: boolean = false
): { [key: string]: ActorDescriptor } => {
  const participant = usePartcipantActorDescriptor();
  return useMemo(() => {
    const cs = ACTOR_COLORS_LIGHT.map((c) => c.hex);
    const actorPool = includeArchived
      ? Object.values(supportingActors)
      : getSelectableSupportingActors(supportingActors);
    return keyBy(
      uniqBy(
        [
          participant,
          // just take a guess here that manager and mentor are likely supporting actors, that
          // are probably more important than other roles
          ...sortBy(actorPool, (a) =>
            a.key === 'manager' || a.key === 'mentor' ? '0' : a.name
          )
            .filter(
              (sa) =>
                includeChannelActors || sa.type !== SupportingActorType.CHANNEL
            )
            .map((a) => ({
              key: a.key,
              label: a.name,
              color: a.color,
              type: a.type
            }))
        ],
        (d) => d.key
      ).map((d, i) => ({ ...d, color: d.color || cs[i % cs.length] })),
      (d) => d.key
    );
  }, [participant, supportingActors, includeChannelActors, includeArchived]);
};

const SingleActor = styled(ButtonBase)((p) => ({
  '&&': {
    padding: `${p.theme.spacing(0.25)}px ${p.theme.spacing()}px`,
    margin: `-${p.theme.spacing(0.25)}px -${p.theme.spacing()}px`, // keeps the alignment
    borderRadius: p.theme.custom.border.radius(),
    border: '1px solid transparent',

    '&:hover': {
      backgroundColor: p.theme.custom.colors.blue.w100,
      borderColor: p.theme.custom.colors.blue.w200
    }
  }
}));

// this will also be able to do filtering later
export const ActorLegend = React.memo(
  ({
    descriptors,
    children,
    onClick
  }: {
    descriptors: { [key: string]: ActorDescriptor };
    children?: React.ReactNode;
    onClick?: (actorKey: string) => void;
  }) => {
    const actors = useMemo(() => Object.values(descriptors), [descriptors]);
    return (
      <>
        {actors.map((d) => {
          const b = (
            <WithShape key={d.key} shape="square" color={d.color}>
              <Typography variant="body1">
                <strong>{d.label}</strong>
              </Typography>
            </WithShape>
          );
          if (!onClick || d.key === 'participant') {
            return b;
          }
          return <SingleActor onClick={() => onClick(d.key)}>{b}</SingleActor>;
        })}
        {children}
      </>
    );
  }
);

// Clickable wrapper for the new-timeline actor filter. Renders the shared
// `ActorChip` exactly as the task modal's assignee chip does (actor color +
// dark text; tinted when the color predates the v4 palette). Selection state
// is conveyed by fading the whole chip when the actor is filtered out. A
// trailing status icon is always reserved (like the legacy actor pill) so the
// chip never changes width: a check while shown, swapped for a "show only" eye
// on hover.
export const ActorFilterChipButton = styled('button')<{ selected: boolean }>`
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  opacity: ${(p) => (p.selected ? 1 : 0.4)};
  transition: opacity 120ms ease;
  #af-icon {
    display: inline-flex;
    align-items: center;
  }
  #af-eye {
    display: none;
    align-items: center;
  }
  &:hover {
    opacity: 1;
  }
  &:hover #af-icon {
    display: none;
  }
  &:hover #af-eye {
    display: inline-flex;
  }
`;

// An actor color that predates the v4 palette is rendered tinted (matching the
// assignee chip), so old and new colors stay visually distinguishable.
const isLegacyActorColor = (color: string) =>
  !ACTOR_COLORS_LIGHT.some((c) => c.hex.toLowerCase() === color.toLowerCase());

const ActorFilterChip = ({
  isSelected,
  d,
  onClick,
  newStyle = false
}: {
  isSelected: boolean;
  d: ActorDescriptor;
  onClick: (d: string, selectSinlge: boolean) => void;
  // Opt-in treatment for the new timeline view: renders the shared `ActorChip`
  // (faded when the actor is filtered out). Classic surfaces omit this and keep
  // the original solid-filled pill.
  newStyle?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const { label } = d;

  if (newStyle) {
    return (
      <Tooltip
        title={
          isSelected
            ? t('Hide {{ label }}', { label })
            : t('Also show {{ label }}', { label })
        }
      >
        <ActorFilterChipButton
          type="button"
          selected={isSelected}
          onClick={() => onClick(d.key, false)}
        >
          <ActorChip color={d.color} dimmed={isLegacyActorColor(d.color)}>
            {label}
            {isSelected && (
              <span id="af-icon">
                <Check size={16} />
              </span>
            )}
            <span id="af-eye">
              <Tooltip title={`${t('Show only {{ label }}', { label })}`}>
                <span style={{ display: 'inline-flex' }}>
                  <Eye
                    size={16}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onClick(d.key, true);
                    }}
                  />
                </span>
              </Tooltip>
            </span>
          </ActorChip>
        </ActorFilterChipButton>
      </Tooltip>
    );
  }

  const textColor = 'white';
  const bgColor = d.color;

  return (
    <ChipWithAction
      onClick={() => onClick(d.key, false)}
      label={
        <Tooltip
          title={`${
            isSelected
              ? t('Hide {{ label }}', { label })
              : t('Also show {{ label }}', { label })
          }`}
        >
          <div>{label}</div>
        </Tooltip>
      }
      color={textColor}
      backgroundColor={bgColor}
      icon={isSelected ? <Check color={textColor} size={16} /> : undefined}
      hoverIcon={
        <Tooltip title={`${t('Show only {{ label }}', { label })}`}>
          <span>
            <Eye
              // Not using an IconButton on purpose, as it's messes vertical positioning up
              color={textColor}
              size={16}
              onClick={(ev) => {
                ev.stopPropagation();
                onClick(d.key, true);
              }}
            />
          </span>
        </Tooltip>
      }
    />
  );
};

export const ActorLegendV2 = React.memo(
  ({
    descriptors,
    selected,
    setSelected,
    hasAutomation,
    newStyle = false
  }: {
    descriptors: { [key: string]: ActorDescriptor };
    selected: Set<string>;
    setSelected: (nextSelected: Set<string>) => void;
    hasAutomation: boolean;
    // Classic surfaces keep the solid-filled pills; the new timeline view opts
    // into the tinted/dot treatment.
    newStyle?: boolean;
  }) => {
    const { t } = useUnsafeTranslation();
    const theme = useTheme();

    const options = useMemo(() => {
      const actors = Object.values(descriptors);
      return hasAutomation
        ? [
            ...actors,
            {
              key: 'AUTOMATION',
              label: t('Automation'),
              color: theme.custom.colors.purple.w500,
              type: 'AUTOMATION'
            } as ActorDescriptor
          ]
        : actors;
    }, [descriptors, hasAutomation, theme, t]);

    const allSelected = useMemo(
      () => selected.size === 0 || selected.size === options.length,
      [selected, options]
    );

    const onSelect = (key: string, selectSingle: boolean) => {
      if (selectSingle) {
        setSelected(new Set([key]));
      } else {
        const nextFilter = selected.size
          ? new Set([...selected])
          : new Set(options.map((x) => x.key));
        if (nextFilter.has(key)) {
          nextFilter.delete(key);
        } else {
          nextFilter.add(key);
        }
        setSelected(nextFilter);
      }
    };
    return (
      <FlexContainer wrap="wrap">
        {options.map((d) => (
          <ActorFilterChip
            key={d.key}
            d={d}
            isSelected={allSelected || selected.has(d.key)}
            onClick={onSelect}
            newStyle={newStyle}
          />
        ))}
        {!allSelected && (
          <Button
            color="primary"
            size="small"
            onClick={() => setSelected(new Set())}
          >
            {t('Show all')}
          </Button>
        )}
      </FlexContainer>
    );
  }
);

export const ActorLegendEditable = React.memo(
  ({
    orgId,
    descriptors,
    onChange
  }: {
    orgId: string;
    descriptors: { [key: string]: ActorDescriptorExtended };
    onChange: (key: string, nextUser: Doc<IUser>) => void;
  }) => {
    const actors = useMemo(() => Object.values(descriptors), [descriptors]);
    return (
      <>
        {actors.map((d) => (
          <CustomInputLabel
            label={
              <WithShape key={d.key} shape="square" color={d.color} wrap>
                <strong>{d.label}</strong>
              </WithShape>
            }
          >
            {d.type !== SupportingActorType.CHANNEL && (
              <AutocompleteSingle
                fullWidth
                value={d.user}
                onChange={(nextUser) => {
                  nextUser && onChange(d.key, nextUser);
                }}
                getOptionLabel={(u) => toFullName(u.data)}
                options={() => getUsersByOrgId(orgId)}
                filterOptions={(us, s) => {
                  const t = toSearchTermRegexp(s.inputValue);
                  return us.filter(
                    (u) =>
                      u.id !== descriptors[PARTICIPANT_ACTOR_KEY]?.user?.id &&
                      matchUserBySearchTerm(t, u)
                  );
                }}
                disabledClearable
                inputProps={{
                  style: { width: 'fit-content' }
                }}
              />
            )}
          </CustomInputLabel>
        ))}
      </>
    );
  }
);
